interface OptimizeImageOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  format?: "jpeg" | "png" | "webp";
  maintainAspectRatio?: boolean;
  preserveTransparency?: boolean;
}

interface OptimizeImageResult {
  dataURL: string;
  format: string;
  width: number;
  height: number;
  originalSize: number;
  optimizedSize: number;
}

/**
 * Optimizes an image for PDF embedding by resizing and compressing
 * while maintaining visual quality and transparency if needed
 */
export async function optimizeImageForPDF(
  imagePath: string,
  options: OptimizeImageOptions = {},
): Promise<OptimizeImageResult> {
  const {
    maxWidth = 1200,
    maxHeight = 1200,
    quality = 0.8,
    format = "jpeg",
    maintainAspectRatio = true,
    preserveTransparency = false,
  } = options;

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      try {
        // Calculate dimensions maintaining aspect ratio
        const { width, height } = calculateOptimalDimensions(
          img.width,
          img.height,
          maxWidth,
          maxHeight,
          maintainAspectRatio,
        );

        // Create high-DPI canvas for better quality
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d", {
          alpha: preserveTransparency,
          imageSmoothingEnabled: true,
          imageSmoothingQuality: "high",
        }) as CanvasRenderingContext2D;

        // Set canvas size
        canvas.width = width;
        canvas.height = height;

        // Enable high-quality image smoothing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";

        // Fill background for JPEG (no transparency)
        if (format === "jpeg" && !preserveTransparency) {
          ctx.fillStyle = "#FFFFFF";
          ctx.fillRect(0, 0, width, height);
        }

        // Draw the image with high-quality scaling
        ctx.drawImage(img, 0, 0, width, height);

        // Apply additional optimization for PNG with transparency
        if (format === "png" && preserveTransparency) {
          optimizePNGTransparency(ctx, width, height);
        }

        // Convert to optimized format
        const outputFormat = preserveTransparency ? "png" : format;
        const dataURL = canvas.toDataURL(
          `image/${outputFormat}`,
          format === "jpeg" ? quality : undefined,
        );

        // Calculate file sizes for comparison
        const originalSize = estimateImageSize(img.width, img.height, "png");
        const optimizedSize = estimateDataURLSize(dataURL);

        resolve({
          dataURL,
          format: outputFormat,
          width,
          height,
          originalSize,
          optimizedSize,
        });
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => reject(new Error(`Failed to load image: ${imagePath}`));
    img.src = imagePath;
  });
}

/**
 * Calculate optimal dimensions while maintaining aspect ratio
 */
function calculateOptimalDimensions(
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight: number,
  maintainAspectRatio: boolean,
): { width: number; height: number } {
  if (!maintainAspectRatio) {
    return { width: maxWidth, height: maxHeight };
  }

  const aspectRatio = originalWidth / originalHeight;

  let width = originalWidth;
  let height = originalHeight;

  // Scale down if too large
  if (width > maxWidth) {
    width = maxWidth;
    height = width / aspectRatio;
  }

  if (height > maxHeight) {
    height = maxHeight;
    width = height * aspectRatio;
  }

  return {
    width: Math.round(width),
    height: Math.round(height),
  };
}

/**
 * Optimize PNG transparency by reducing color palette
 */
function optimizePNGTransparency(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
): void {
  try {
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;

    // Simple alpha optimization - remove near-transparent pixels
    for (let i = 0; i < data.length; i += 4) {
      const alpha = data[i + 3];

      // Convert nearly transparent pixels to fully transparent
      if (alpha < 25) {
        data[i + 3] = 0;
      }
      // Convert nearly opaque pixels to fully opaque
      else if (alpha > 230) {
        data[i + 3] = 255;
      }
    }

    ctx.putImageData(imageData, 0, 0);
  } catch (error) {
    // If optimization fails, continue with original image
    console.warn("PNG transparency optimization failed:", error);
  }
}

/**
 * Estimate image file size based on dimensions and format
 */
function estimateImageSize(
  width: number,
  height: number,
  format: string,
): number {
  const pixels = width * height;

  switch (format) {
    case "png":
      return pixels * 4; // 4 bytes per pixel (RGBA)
    case "jpeg":
      return pixels * 3 * 0.1; // Rough JPEG compression estimate
    default:
      return pixels * 3;
  }
}

/**
 * Estimate data URL size in bytes
 */
function estimateDataURLSize(dataURL: string): number {
  // Remove data URL prefix and calculate base64 size
  const base64Data = dataURL.split(",")[1];
  return (base64Data.length * 3) / 4;
}

/**
 * Batch optimize multiple images for PDF
 */
export async function optimizeImagesForPDF(
  imagePaths: string[],
  options: OptimizeImageOptions = {},
): Promise<OptimizeImageResult[]> {
  const results = await Promise.allSettled(
    imagePaths.map((path) => optimizeImageForPDF(path, options)),
  );

  return results
    .filter(
      (result): result is PromiseFulfilledResult<OptimizeImageResult> =>
        result.status === "fulfilled",
    )
    .map((result) => result.value);
}

/**
 * Smart format selection based on image characteristics
 */
export async function detectOptimalFormat(
  imagePath: string,
): Promise<"jpeg" | "png"> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d")!;

      canvas.width = Math.min(img.width, 100);
      canvas.height = Math.min(img.height, 100);

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      try {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        let transparentPixels = 0;
        const totalPixels = data.length / 4;

        for (let i = 3; i < data.length; i += 4) {
          if (data[i] < 255) {
            transparentPixels++;
          }
        }

        const transparencyRatio = transparentPixels / totalPixels;

        // Use PNG if more than 1% of pixels have transparency
        resolve(transparencyRatio > 0.01 ? "png" : "jpeg");
      } catch {
        // Default to JPEG if detection fails
        resolve("jpeg");
      }
    };

    img.onerror = () => resolve("jpeg");
    img.src = imagePath;
  });
}
